import { useQuery } from '@tanstack/react-query';
import { formsApi, type FormEntity, type Field } from '../features/forms/api.ts';
import { dv } from '../i18n/dv.ts';

/** Binds a system field (by its `key`) to an external value instead of customData. */
export interface SystemValueBinding {
  value: string;
  onChange: (v: string) => void;
}

interface CustomFieldsProps {
  entity: FormEntity;
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  disabled?: boolean;
  /**
   * Optional overrides for system fields, keyed by `field.key`. When a field has a
   * matching key, it renders a text input bound to this {value,onChange} pair
   * (e.g. the seeded "Full name" field bound to Person.fullName) and ignores
   * customData for that field.
   */
  systemValues?: Record<string, SystemValueBinding>;
}

function asString(v: unknown): string {
  return v == null ? '' : String(v);
}

function asStringArray(v: unknown): string[] {
  return Array.isArray(v) ? v.map((x) => String(x)) : [];
}

function FieldLabel({ field }: { field: Field }): JSX.Element {
  return (
    <label className="field-label">
      {field.label}
      {field.required && <span className="ms-1 text-destructive" aria-hidden>*</span>}
    </label>
  );
}

function CustomField({
  field, value, onChange, disabled, systemBinding,
}: {
  field: Field;
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  disabled?: boolean;
  systemBinding?: SystemValueBinding;
}): JSX.Element {
  const current = value[field.id];
  const set = (newVal: unknown): void => onChange({ ...value, [field.id]: newVal });
  const options = field.options ?? [];

  // System-bound fields (e.g. Full name → Person.fullName) read/write the external
  // binding rather than customData. Always rendered as a text input.
  if (systemBinding) {
    return (
      <div className="space-y-1">
        <FieldLabel field={field} />
        <input
          className="input"
          dir="auto"
          disabled={disabled}
          required={field.required}
          value={systemBinding.value}
          onChange={(e) => systemBinding.onChange(e.target.value)}
        />
      </div>
    );
  }

  switch (field.type) {
    case 'NUMBER':
      return (
        <div className="space-y-1">
          <FieldLabel field={field} />
          <input
            type="number"
            dir="ltr"
            className="input"
            disabled={disabled}
            required={field.required}
            value={asString(current)}
            onChange={(e) => set(e.target.value === '' ? '' : Number(e.target.value))}
          />
        </div>
      );
    case 'DATE':
      return (
        <div className="space-y-1">
          <FieldLabel field={field} />
          <input
            type="date"
            dir="ltr"
            className="input"
            disabled={disabled}
            required={field.required}
            value={asString(current).slice(0, 10)}
            onChange={(e) => set(e.target.value)}
          />
        </div>
      );
    case 'SELECT':
      return (
        <div className="space-y-1">
          <FieldLabel field={field} />
          <select
            className="input"
            disabled={disabled}
            required={field.required}
            value={asString(current)}
            onChange={(e) => set(e.target.value)}
          >
            <option value="">{dv.common.none}</option>
            {options.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </div>
      );
    case 'CHOICE':
      return (
        <fieldset className="space-y-1">
          <FieldLabel field={field} />
          <div className="space-y-1">
            {options.map((o) => (
              <label key={o} className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name={field.id}
                  disabled={disabled}
                  checked={asString(current) === o}
                  onChange={() => set(o)}
                />
                <span>{o}</span>
              </label>
            ))}
          </div>
        </fieldset>
      );
    case 'MULTISELECT': {
      const selected = asStringArray(current);
      const toggle = (o: string): void => {
        set(selected.includes(o) ? selected.filter((x) => x !== o) : [...selected, o]);
      };
      return (
        <fieldset className="space-y-1">
          <FieldLabel field={field} />
          <div className="space-y-1">
            {options.map((o) => (
              <label key={o} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  disabled={disabled}
                  checked={selected.includes(o)}
                  onChange={() => toggle(o)}
                />
                <span>{o}</span>
              </label>
            ))}
          </div>
        </fieldset>
      );
    }
    case 'TEXT':
    default:
      return (
        <div className="space-y-1">
          <FieldLabel field={field} />
          <input
            className="input"
            dir="auto"
            disabled={disabled}
            required={field.required}
            value={asString(current)}
            onChange={(e) => set(e.target.value)}
          />
        </div>
      );
  }
}

export function CustomFields({ entity, value, onChange, disabled, systemValues }: CustomFieldsProps): JSX.Element | null {
  const { data: sections } = useQuery({
    queryKey: ['form-fields', entity],
    queryFn: () => formsApi.list(entity),
  });

  const withFields = (sections ?? []).filter((s) => s.fields.length > 0);
  if (withFields.length === 0) return null;

  // Each admin-defined section renders as its own card, titled with the section
  // name (e.g. "Education") — so the page is driven entirely by the built form.
  return (
    <>
      {withFields.map((section) => (
        <section key={section.id} className="card-paper space-y-3 p-4">
          <h2 className="font-serif text-lg font-bold leading-tight">{section.label}</h2>
          {section.fields.map((field) => (
            <CustomField
              key={field.id}
              field={field}
              value={value}
              onChange={onChange}
              disabled={disabled}
              systemBinding={field.key ? systemValues?.[field.key] : undefined}
            />
          ))}
        </section>
      ))}
    </>
  );
}
